import React from 'react';
import { Link } from 'react-router-dom';

import bannerLogo from '../images/Jook_Web_White.png';
import { analyticsEnabled, setConsent, storedConsent } from '../utils/Analytics';

import { COMPANY_NAME, CONTACT_EMAIL } from '../config/contact';

import './LegalView.scss';

// The Terms of Service and Privacy Policy, at /terms and /privacy. New accounts agree to
// both at sign-up, on the web and in the phone app, which links here.
//
// This is boilerplate. It has not been reviewed by a lawyer, and the bracketed parts are
// still to be filled in: [state], and [14 months], the Google Analytics retention, which
// must match the GA property's setting. When the text changes, change TERMS_VERSION in
// src/config/legal.ts and LAST_UPDATED below together.

const LAST_UPDATED = 'October 6, 2026';

interface Section {
  heading: string;
  paragraphs: string[];
}

const TERMS: Section[] = [
  {
    heading: 'Who we are',
    paragraphs: [
      `Jookable is a video jukebox. Venues put a Jookable player on a screen, and guests use their phone or computer to add YouTube videos to its queue. Jookable is run by ${COMPANY_NAME} ("Jookable", "we", "us").`,
      'By creating an account or using Jookable you agree to these Terms and to our Privacy Policy. If you do not agree, do not use Jookable.',
    ],
  },
  {
    heading: 'Your account',
    paragraphs: [
      'You must be at least 13 years old to make an account, and old enough to form a binding contract where you live to buy bumps. Give us a real email address and keep your password to yourself. You are responsible for what happens on your account.',
      'Pick a username that is not offensive and does not pretend to be someone else. We may change or remove usernames and profile photos that break these Terms.',
    ],
  },
  {
    heading: 'Using a jukebox',
    paragraphs: [
      "Each venue sets its own rules for its jukebox, such as how many songs a person can queue and which videos are allowed. The venue can remove any song from its queue, for any reason.",
      'Do not queue content that is illegal, hateful, sexually explicit, or that the venue has asked you not to play. Do not try to disrupt a jukebox, get around its limits, or access accounts or data that are not yours.',
    ],
  },
  {
    heading: 'YouTube content',
    paragraphs: [
      'Videos on Jookable are played from YouTube through the YouTube API Services. Jookable does not host, own or license these videos. By using Jookable you also agree to the YouTube Terms of Service (https://www.youtube.com/t/terms), and Google\'s Privacy Policy (https://policies.google.com/privacy) applies to videos you watch or search for.',
      'A video can stop being available at any time, for example if its owner makes it private or YouTube removes it. We are not responsible for what a video contains.',
    ],
  },
  {
    heading: 'Copyright and licensing',
    paragraphs: [
      'Jookable does not hold or provide any license to publicly perform, display or broadcast music or video. Each person or entity that runs a jukebox, or plays, queues or shows content through Jookable, assumes all responsibility for getting, paying for and keeping in force every copyright and performance license that use needs. That includes licenses from performing rights organizations such as ASCAP, BMI, SESAC and GMR, or their equivalents where you are, and any licenses or permissions from the content owners.',
      `You agree that Jookable is not responsible for your licensing, and that you will defend, indemnify and hold harmless Jookable and ${COMPANY_NAME} from any claim, fine, fee or damages that arise from content played, queued or shown through your account or at your venue.`,
    ],
  },
  {
    heading: 'Bumps and super bumps',
    paragraphs: [
      'A bump moves your song up a venue\'s queue. A super bump moves it further. Some are free with a new account; more can be bought in the app, on the web, or from the Apple App Store or Google Play.',
      'Bumps are a way to move songs up a queue. They are not money, have no cash value, cannot be transferred or sold, and cannot be exchanged for cash. A bump is used up when you use it, even if the venue later removes the song, skips it, or the video stops playing.',
      'All purchases are final, except where the law says otherwise or where we decide to give a refund. Purchases made through the Apple App Store or Google Play are handled under those stores\' own refund rules, and refund requests for them go to Apple or Google.',
      'We may change how bumps work, what they cost, or stop offering them. If we stop Jookable entirely, unused bumps end with it.',
    ],
  },
  {
    heading: 'Payments',
    paragraphs: [
      'Payments on the web are processed by Stripe. Payments in the phone app are processed by Apple or Google. We never see or store your full card number.',
      'Prices are shown before you pay and may include taxes where required.',
    ],
  },
  {
    heading: 'Venues and payouts',
    paragraphs: [
      'If you run a jukebox you are responsible for what plays on it, for following the law where it is shown, for the settings you choose, and for the copyright and performance licenses described under Copyright and licensing.',
      'Venue owners may receive a share of bump sales made on their jukebox, paid out through Stripe Connect. To be paid you must set up a Stripe account and accept Stripe\'s own terms. Until you do, Jookable keeps the whole sale. We may change the share, and we may hold or withdraw payouts that look fraudulent or break these Terms.',
    ],
  },
  {
    heading: 'Profile photos and venue logos',
    paragraphs: [
      'You can upload a profile photo, and venue owners can upload a logo. Only upload images you have the right to use. You keep ownership of them, and you give us permission to store them and show them in Jookable while they are on your account. We may remove any image that breaks these Terms.',
    ],
  },
  {
    heading: 'Deleting your account',
    paragraphs: [
      'You can delete your account at any time from your account settings on the web or in the app. Deleting your account removes your profile, profile photo, favorites and any jukeboxes you own, and ends any unused bumps. It cannot be undone. See the Privacy Policy for the few records we keep.',
      'We may suspend or close accounts that break these Terms, misuse Jookable, or put other people at risk.',
    ],
  },
  {
    heading: 'No warranty',
    paragraphs: [
      'Jookable is provided "as is" and "as available". We do not promise it will always work, be free of errors, or that any video will be available. To the fullest extent the law allows, we disclaim all warranties, express or implied.',
    ],
  },
  {
    heading: 'Limit of liability',
    paragraphs: [
      'To the fullest extent the law allows, Jookable is not liable for any indirect, incidental, special or consequential damages, or for lost profits or data. Our total liability for any claim about Jookable is limited to the amount you paid us in the 12 months before the claim, or $50, whichever is more.',
    ],
  },
  {
    heading: 'Changes',
    paragraphs: [
      'We may update these Terms. If a change is significant we will tell you in the app or by email. Using Jookable after a change means you accept the new Terms.',
    ],
  },
  {
    heading: 'Governing law and contact',
    paragraphs: [
      'These Terms are governed by the laws of [state], United States, without regard to its conflict of law rules.',
      `Questions about these Terms: ${CONTACT_EMAIL}.`,
    ],
  },
];

const PRIVACY: Section[] = [
  {
    heading: 'Overview',
    paragraphs: [
      `This policy explains what Jookable, run by ${COMPANY_NAME}, collects about you, why, who we share it with, and the choices you have. It covers the Jookable website, player screens and phone apps.`,
    ],
  },
  {
    heading: 'What we collect',
    paragraphs: [
      'Account details: your username, email address and password. Passwords are stored only as a secure hash, never as plain text.',
      'Profile photo: if you upload one. Venue owners can also upload a logo for their jukebox.',
      'What you do on Jookable: the videos you search for, queue, bump and favorite, the jukeboxes you join, and when. Your username is shown next to songs you queue, to other people at that venue.',
      'Purchases: which bump packs you bought, when, the price, and the payment reference from Stripe, Apple or Google. We do not receive or store your full card number.',
      'Venue payouts: if you run a jukebox and set up payouts, Stripe collects your identity and bank details directly. We store only the ID of your Stripe account.',
      'Device and technical data: your IP address (used to stop abuse and limit sign-ups and searches), the type of device you sign in from, and sign-in tokens so you stay signed in. Our website stores your sign-in and a few settings, such as the venue logo on a player screen, in your browser\'s local storage.',
      'Analytics: we use Google Analytics to understand how Jookable is used. On our website it sets cookies and collects the pages you view, the jukebox you use, the songs you search for, add and favorite, when you use or buy bumps and how much you paid, how long you stay, your device, browser and operating system, and your approximate location (city or region, worked out from your IP address). It does not receive your name, email, username or account ID. With Google Signals turned on, Google may also give us estimated age range, gender and interests for visitors signed in to a Google account that allows it. We see this only as totals and trends, not tied to your Jookable account.',
    ],
  },
  {
    heading: 'How we use it',
    paragraphs: [
      'To run your account and the jukeboxes you use, to process purchases and pay venue owners their share, to send emails you ask for (such as password resets and jukebox invites), to prevent fraud and abuse, to keep Jookable secure, and to meet legal obligations.',
      'We do not sell your personal information, and we do not show ads in Jookable.',
    ],
  },
  {
    heading: 'Who we share it with',
    paragraphs: [
      'Venues: the owner and admins of a jukebox can see the usernames on its queue and the sales made on it.',
      'YouTube: searches and video playback go through the YouTube API Services, so Google receives your searches and the videos played. Google\'s Privacy Policy applies: https://policies.google.com/privacy. You can revoke Jookable\'s access to YouTube data through Google\'s security settings at https://security.google.com/settings/security/permissions.',
      'Payment processors: Stripe (web payments and venue payouts), Apple and Google (in-app purchases).',
      'Google Analytics: Google receives the analytics data described above. See how Google uses it at https://policies.google.com/technologies/partner-sites. You can opt out with the Google Analytics opt-out browser add-on at https://tools.google.com/dlpage/gaoptout, by blocking cookies in your browser, or by turning off ad personalization at https://adssettings.google.com.',
      'Service providers who help us run Jookable, such as hosting and email delivery, under agreements to protect your data.',
      'The law: when required by law, or to protect the rights and safety of our users or others.',
    ],
  },
  {
    heading: 'How long we keep it',
    paragraphs: [
      'We keep your account details for as long as you have an account. Google Analytics data is kept for [14 months], then deleted by Google.',
      'When you delete your account we remove your profile, email, password, profile photo, favorites and any jukeboxes you own. Songs you had already queued at other venues stay in those queues without your name. Purchase records stay with your name removed, because we need them for accounting, tax and to stop a purchase being credited twice. Backups are overwritten on a regular cycle.',
    ],
  },
  {
    heading: 'Your choices and rights',
    paragraphs: [
      `You can change your username, password and profile photo in your account settings, and delete your account at any time from the web or the app. To change your email address, email ${CONTACT_EMAIL}.`,
      `Depending on where you live you may have the right to access, correct, delete or get a copy of your data, and to object to how we use it. To ask, email ${CONTACT_EMAIL}. We will not treat you differently for using these rights.`,
      'Where the law requires it, such as in the EU and UK, we ask before setting analytics cookies, and you can change your mind at any time.',
    ],
  },
  {
    heading: 'Children',
    paragraphs: [
      `Jookable is not meant for children under 13, and we do not knowingly collect their information. If you think a child under 13 has made an account, email ${CONTACT_EMAIL} and we will delete it.`,
    ],
  },
  {
    heading: 'Security',
    paragraphs: [
      'We use reasonable measures to protect your information, including encrypted connections and hashed passwords. No system is perfectly secure, so we cannot guarantee it.',
    ],
  },
  {
    heading: 'Changes and contact',
    paragraphs: [
      'We may update this policy. If a change is significant we will tell you in the app or by email.',
      `Questions about your privacy: ${CONTACT_EMAIL}.`,
    ],
  },
];

const DOCUMENTS = {
  terms: { title: 'Terms of Service', sections: TERMS, other: { to: '/privacy', label: 'Privacy Policy' } },
  privacy: { title: 'Privacy Policy', sections: PRIVACY, other: { to: '/terms', label: 'Terms of Service' } },
};

// Turns the http(s) addresses and the contact email in a paragraph into links.
const withLinks = (text: string) =>
  text.split(/(https?:\/\/[^\s)]+|[\w.+-]+@[\w-]+\.[\w.-]*\w)/).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      <a key={index} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : /@/.test(part) ? (
      <a key={index} href={`mailto:${part}`}>
        {part}
      </a>
    ) : (
      part
    ),
  );

/**
 * Lets a visitor change their analytics answer, as the policy promises. Only on the privacy
 * page, and only when analytics is switched on for this site.
 */
const AnalyticsChoice = () => {
  const [choice, setChoice] = React.useState(storedConsent());
  if (!analyticsEnabled()) {
    return null;
  }
  const answer = (value: 'granted' | 'denied') => {
    setConsent(value);
    setChoice(value);
  };
  return (
    <section>
      <h2 className="legal-heading">Your analytics choice</h2>
      <p className="legal-text">
        {choice === 'granted' && 'Analytics cookies are allowed in this browser.'}
        {choice === 'denied' && 'Analytics cookies are turned off in this browser.'}
        {!choice && 'You have not made a choice in this browser yet.'}
      </p>
      <div className="legal-choice">
        <button type="button" disabled={choice === 'granted'} onClick={() => answer('granted')}>
          Allow analytics
        </button>
        <button type="button" disabled={choice === 'denied'} onClick={() => answer('denied')}>
          Turn off analytics
        </button>
      </div>
    </section>
  );
};

interface LegalViewProps {
  document: 'terms' | 'privacy';
}

const LegalView = ({ document }: LegalViewProps) => {
  const { title, sections, other } = DOCUMENTS[document];

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [document]);

  return (
    <div className="legal">
      <div className="legal-header">
        <Link to="/">
          <img className="legal-logo" src={bannerLogo} alt="Jookable" />
        </Link>
      </div>

      <div className="legal-body">
        <div className="legal-panel">
          <h1 className="legal-title">{title}</h1>
          <p className="legal-updated">Last updated {LAST_UPDATED}</p>

          {sections.map(section => (
            <section key={section.heading}>
              <h2 className="legal-heading">{section.heading}</h2>
              {section.paragraphs.map((paragraph, index) => (
                <p key={index} className="legal-text">
                  {withLinks(paragraph)}
                </p>
              ))}
            </section>
          ))}

          {document === 'privacy' && <AnalyticsChoice />}

          <div className="legal-links">
            <Link className="legal-link" to={other.to}>
              {other.label}
            </Link>
            <Link className="legal-link" to="/">
              Back to Jookable
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalView;
